import React from "react";
import Modal from "./Modal";
import Stepper from "./Stepper";
import SplitPie from "./SplitPie";
import { ItemUI } from "../types/item";
import { User } from "../types/user";
import "./SplitControlsModal.scss";

interface SplitControlsModalProps {
  item: ItemUI;
  selectedUser: User | null;
  // to name who holds each portion
  usersById: Map<string, User>;
  // the selected user's portions, and the most they can hold (theirs + open)
  myShares: number;
  myMax: number;
  claimed: number;
  canEditShares: boolean;
  canEditSplits: boolean;
  onSetShare: (shares: number) => void;
  onSetSplits: (splits: number) => void;
  handleClose: () => void;
}

// One item's split controls: who holds it, the selected mate's portions and
// the split count. Steppers apply live (same debounced writes as before), so
// there's no Save — just Done.
const SplitControlsModal: React.FC<SplitControlsModalProps> = ({
  item,
  selectedUser,
  usersById,
  myShares,
  myMax,
  claimed,
  canEditShares,
  canEditSplits,
  onSetShare,
  onSetSplits,
  handleClose,
}) => {
  const splits = Math.max(1, item.splits);
  const amountFor = (portions: number) =>
    `$${((item.price * portions) / splits).toFixed(2)}`;
  const rosterRank = new Map(Array.from(usersById.keys(), (id, i) => [id, i]));
  const rankOf = (id: string) => rosterRank.get(id) ?? Infinity;
  const holders = Object.entries(item.shares)
    .filter(([, portions]) => portions > 0)
    .sort(([idA, a], [idB, b]) => b - a || rankOf(idA) - rankOf(idB));
  const open = Math.max(0, splits - claimed);

  return (
    <Modal
      className="split-controls-modal"
      title={item.name || "Item"}
      handleClose={handleClose}
    >
      <div className="split-controls-summary">
        <SplitPie
          splits={item.splits}
          claimed={claimed}
          mine={myShares}
          size={40}
        />
        <span className="split-controls-price">${item.price.toFixed(2)}</span>
      </div>

      {selectedUser && (
        <div className="ctrl">
          <span className="ctrl-label">{selectedUser.name}'s portions</span>
          <Stepper
            value={myShares}
            min={0}
            max={myMax}
            onChange={onSetShare}
            disabled={!canEditShares}
          />
        </div>
      )}
      <div className="ctrl">
        <span className="ctrl-label">Split</span>
        {canEditSplits ? (
          <Stepper value={item.splits} min={1} onChange={onSetSplits} />
        ) : (
          <span className="ctrl-static">{item.splits}</span>
        )}
      </div>

      {/* who holds it: one row per mate with portions, then what's still open */}
      <div className="split-holders">
        {holders.length === 0 ? (
          <span className="holder-empty">No one has claimed this yet</span>
        ) : (
          holders.map(([userId, portions]) => (
            <div
              key={userId}
              className={`holder-row${
                userId === selectedUser?.id ? " selected" : ""
              }`}
            >
              <span className="holder-name">
                {usersById.get(userId)?.name ?? "Unknown"}
              </span>
              <span className="holder-fraction">
                {portions}/{splits}
              </span>
              <span className="holder-amount">{amountFor(portions)}</span>
            </div>
          ))
        )}
        {holders.length > 0 && open > 0 && (
          <div className="holder-row open">
            <span className="holder-name">Open</span>
            <span className="holder-fraction">
              {open}/{splits}
            </span>
            <span className="holder-amount">{amountFor(open)}</span>
          </div>
        )}
      </div>

      <button className="modal-button" onClick={handleClose}>
        Done
      </button>
    </Modal>
  );
};

export default SplitControlsModal;
